/**
 * Contrato de sessão compartilhado com o SPA Vanilla (`projeto/js/core/Api.js`).
 * Mesmas chaves em sessionStorage → login React e legado convivem no mesmo origin.
 */

export const SESSION_KEYS = {
  access: '_at',
  refresh: '_rt',
  user: 'hiRH_user',
} as const

/** Disparado no mesmo tab após set/clear de tokens (storage event só cobre outros tabs). */
export const SESSION_EVENT = 'hi:session'

export type SessionSnapshot = {
  access: string | null
  refresh: string | null
  hasToken: boolean
}

export function getAccessToken() {
  return sessionStorage.getItem(SESSION_KEYS.access)
}

export function getRefreshToken() {
  return sessionStorage.getItem(SESSION_KEYS.refresh)
}

export function hasToken() {
  return Boolean(getAccessToken())
}

export function getSessionSnapshot(): SessionSnapshot {
  const access = getAccessToken()
  return {
    access,
    refresh: getRefreshToken(),
    hasToken: Boolean(access),
  }
}

export function setTokens(access: string, refresh?: string) {
  sessionStorage.setItem(SESSION_KEYS.access, access)
  if (refresh) sessionStorage.setItem(SESSION_KEYS.refresh, refresh)
  emitSessionChange()
}

export function clearSession() {
  sessionStorage.removeItem(SESSION_KEYS.access)
  sessionStorage.removeItem(SESSION_KEYS.refresh)
  sessionStorage.removeItem(SESSION_KEYS.user)
  emitSessionChange()
}

export function getStoredUser<T = unknown>(): T | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEYS.user)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

export function setStoredUser(user: unknown) {
  if (user) sessionStorage.setItem(SESSION_KEYS.user, JSON.stringify(user))
  else sessionStorage.removeItem(SESSION_KEYS.user)
  emitSessionChange()
}

function emitSessionChange() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(SESSION_EVENT, { detail: getSessionSnapshot() }))
}

/** Escuta mudanças de sessão (mesmo tab + outros tabs no mesmo origin). */
export function subscribeSession(listener: (snap: SessionSnapshot) => void) {
  const onCustom = () => listener(getSessionSnapshot())
  const onStorage = (e: StorageEvent) => {
    if (
      e.storageArea === sessionStorage &&
      (e.key === SESSION_KEYS.access ||
        e.key === SESSION_KEYS.refresh ||
        e.key === SESSION_KEYS.user ||
        e.key === null)
    ) {
      listener(getSessionSnapshot())
    }
  }
  window.addEventListener(SESSION_EVENT, onCustom)
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener(SESSION_EVENT, onCustom)
    window.removeEventListener('storage', onStorage)
  }
}

/**
 * Base do Vanilla.
 * - Produção / compose (:8080): `/legado` no mesmo origin
 * - Override: `VITE_LEGACY_BASE` (path ou origin absoluto)
 */
export function legacyBasePath() {
  const configured = (import.meta.env.VITE_LEGACY_BASE as string | undefined)?.trim()
  if (configured) return configured.replace(/\/$/, '')
  return '/legado'
}

/** URL do login legado. */
export function legacyLoginUrl(redirectHash = '') {
  const q = redirectHash ? `?redirect=${encodeURIComponent(redirectHash)}` : ''
  return `${legacyBasePath()}/login.html${q}`
}

/** Início do SSO Microsoft Entra ID (redirect no servidor). */
export function microsoftLoginUrl() {
  const base = ((import.meta.env.VITE_API_BASE as string | undefined) || '/api/v1').replace(
    /\/$/,
    '',
  )
  return `${base}/accounts/microsoft/login/`
}

/** URL do painel legado (hash router). */
export function legacyAppUrl(hash = '#/') {
  const h = hash.startsWith('#') ? hash : `#${hash}`
  return `${legacyBasePath()}/index.html${h}`
}
